"use client";

import { usePrefetchOnHover } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnHover";
import { usePrefetchOnTouch } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnTouch";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { brandPage } from "@faire/retailer-visitor-shared/paths";
import { ProductPagePaths } from "@faire/retailer-visitor-shared/paths/ProductPage";
import { usePreloadNavigationToPDP } from "@faire/retailer-visitor-shared/views/product/preload/preloadNavigationToPDP";
import { usePreloadClickToPDP } from "@faire/retailer-visitor-shared/views/product/preload/usePreloadClickToPDP";
import { createContext, ReactNode, useCallback, useContext } from "react";

import { ProductLinkProps } from "../../../ProductTile";
import {
  IBrandLinkProps,
  ITileInteractions,
  ITrackingProps,
} from "../../types";

export const TileInteractionsContextProvider = ({
  data,
  trackingProps,
  children,
}: {
  data?: ITileInteractions;
  trackingProps: ITrackingProps;
  children: ReactNode;
}) => {
  const productPageLink =
    data?.props?.productLinkProps?.productUrl ??
    ProductPagePaths.index(trackingProps.productToken ?? "", {
      productOptionToken: trackingProps.selectedProductOptionToken ?? undefined,
      impressionId: trackingProps.impressionId ?? undefined,
    });
  // includes necessary impression tracking and preload functions
  const preloadNavigationToPDP = usePreloadNavigationToPDP();
  const preloadClickToPDP = usePreloadClickToPDP();

  const {
    recordBrandNameClickEvent,
    recordQuickAddSubmitEvent,
    recordQuickAddButtonClickEvent,
    recordChooseOptionClickEvent,
    recordChooseOptionQuantityUpdateSubmitEvent,
    recordProductTileClickEvent,
    recordHeartClickedEvent,
    recordSaveToBoardSubmitEvent,
    recordRemoveFromBoardSubmitEvent,
  } = useProductTileClickImpressions(
    {
      token: trackingProps.productToken,
      impressionId: trackingProps.impressionId,
      isPromoted: trackingProps.isPromoted,
      promotedBy: trackingProps.promotedBy,
    },
    trackingProps.brandToken
  );

  const preload = useCallback(() => {
    preloadNavigationToPDP(
      {
        showVerticalImagery: trackingProps.showVerticalImagery,
        productToken: trackingProps.productToken,
        bestImage: trackingProps.bestImage,
        images: trackingProps.images ?? [],
        brandToken: trackingProps.brandToken,
      },
      productPageLink
    );
  }, [
    preloadNavigationToPDP,
    trackingProps.productToken,
    trackingProps.bestImage,
    trackingProps.images,
    trackingProps.brandToken,
    trackingProps.showVerticalImagery,
    productPageLink,
  ]);

  const onProductClickBase = useCallback(() => {
    recordProductTileClickEvent();
    if (trackingProps.productToken) {
      preloadClickToPDP(trackingProps.productToken);
    }
    preload();
  }, [
    recordProductTileClickEvent,
    preload,
    preloadClickToPDP,
    trackingProps.productToken,
  ]);

  const onProductClickAction = useCallback(
    (productToken?: string) => {
      data?.tileActions?.onProductClick?.(productToken ?? "");
      onProductClickBase();
    },
    [onProductClickBase, data?.tileActions]
  );

  const onBrandClickAction = useCallback(() => {
    recordBrandNameClickEvent();
    data?.tileActions?.onBrandClick?.();
  }, [recordBrandNameClickEvent, data?.tileActions]);

  const { onMouseEnter, onMouseLeave } = usePrefetchOnHover(
    {
      productToken: trackingProps.productToken,
      bestImage: trackingProps.bestImage,
      images: trackingProps.images ?? [],
      brandToken: trackingProps.brandToken,
    },
    productPageLink ?? ""
  );
  const { onTouchStart } = usePrefetchOnTouch(
    {
      productToken: trackingProps.productToken,
      bestImage: trackingProps.bestImage,
      images: trackingProps.images ?? [],
      brandToken: trackingProps.brandToken,
    },
    productPageLink ?? ""
  );

  // base case for product link props
  const productLinkProps: ProductLinkProps = {
    productUrl: productPageLink,
  };

  // base case for brand link props
  const brandLinkProps: IBrandLinkProps = {
    brandUrl: brandPage(trackingProps.brandToken ?? ""),
    brandToken: trackingProps.brandToken ?? "",
  };

  const onQuickAddButtonClick = (newQuantity?: number) => {
    data?.overlayActions?.onQuickAddClick?.(newQuantity);
    recordQuickAddButtonClickEvent();
  };
  const onQuickAddSubmit = (newQuantity?: number) => {
    data?.overlayActions?.onQuickAddSubmit?.(newQuantity);
    recordQuickAddSubmitEvent(newQuantity);
  };

  const onChooseOptionClick = () => {
    data?.overlayActions?.onChooseOptionClick?.();
    recordChooseOptionClickEvent();
  };

  const onChooseOptionQuantityUpdateSubmit = (newQuantity?: number) => {
    data?.overlayActions?.onChooseOptionQuantityUpdateSubmit?.(newQuantity);
    recordChooseOptionQuantityUpdateSubmitEvent(newQuantity);
  };

  const onFavouriteClick = () => {
    data?.overlayActions?.onFavouriteClick?.();
    recordHeartClickedEvent();
  };

  return (
    <TileInteractionsContext.Provider
      value={{
        props: {
          productLinkProps: data?.props?.productLinkProps ?? productLinkProps,
          brandLinkProps: data?.props?.brandLinkProps ?? brandLinkProps,
          productToken: trackingProps.productToken,
        },
        tileActions: {
          onProductClick: onProductClickAction,
          onBrandClick: onBrandClickAction,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          onMouseDown: preload,
          onTouchStart: onTouchStart,
        },
        overlayActions: {
          onFavouriteClick: onFavouriteClick,
          onSaveToBoardSubmit: recordSaveToBoardSubmitEvent,
          onRemoveFromBoardSubmit: recordRemoveFromBoardSubmitEvent,
          onQuickAddClick: onQuickAddButtonClick,
          onQuickAddSubmit: onQuickAddSubmit,
          onChooseOptionClick: onChooseOptionClick,
          onChooseOptionQuantityUpdateSubmit:
            onChooseOptionQuantityUpdateSubmit,
        },
      }}
    >
      {children}
    </TileInteractionsContext.Provider>
  );
};

const TileInteractionsContext = createContext<ITileInteractions | undefined>(
  undefined
);

export const useTileInteractions = () => {
  const context = useContext(TileInteractionsContext);
  if (!context) {
    throw new Error(
      "useTileInteractions must be used within a TileInteractionsContextProvider"
    );
  }
  return context;
};
